// "Filter your Territory" — cross-filter panel (v3).
//
// Layout mirrors the mock brief:
//
//   ┌──────────────── FILTER YOUR TERRITORY ─────────────────┐
//   │  Filter across care group, service type and specialism │
//   ├─────────────┬──────────────┬──────────────┤            │
//   │ CARE GROUP  │ SERVICE TYPE │ SPECIALISM   │  ← tabs    │
//   │   — Any —   │   — Any —    │   — Any —    │            │
//   ├────────────────────────────────────────────────────────┤
//   │ Select a filter above to view the available options.   │
//   │ or:                                                    │
//   │ [Search …]                                             │
//   │ ─── ranked options list with bars ───                  │
//   └────────────────────────────────────────────────────────┘
//
// Three horizontal category tabs at the top show the currently-picked
// value for each axis (or "— Any —"). Clicking a tab reveals its
// ranked option list beneath (with a search box for long lists). All
// filters combine as AND, so the option counts always reflect the
// pool once the OTHER active filters are applied.
//
// The parent widget owns the three filter states so the map, the
// Territory Pool list and this panel all read a single source of
// truth.
import { useMemo, useState, useEffect } from "react";
import { Filter, Building2, Layers, Heart, Search, ChevronRight, X } from "lucide-react";

const AXES = [
  { id: "care_group",   label: "Care Group",   icon: Building2, listTitle: "Select a Care Group",   searchPh: "Search care groups…"   },
  { id: "service_type", label: "Service Type", icon: Layers,    listTitle: "Select a Service Type", searchPh: "Search service types…" },
  { id: "specialism",   label: "Specialism",   icon: Heart,     listTitle: "Select a Specialism",   searchPh: "Search specialisms…"   },
];

export default function TerritoryFilterPanel({
  providerOptions = [],
  serviceTypeOptions = [],
  specialismOptions = [],
  activeProvider = null,
  activeServiceType = null,
  activeSpecialism = null,
  onSelectProvider = null,
  onSelectServiceType = null,
  onSelectSpecialism = null,
  onClearAll = null,
  filteredCount = 0,
  totalCount = 0,
}) {
  // Which category tab is currently open in the list area? Independent
  // of the filter values — a user can have a filter APPLIED without
  // its tab being open.
  const [openAxis, setOpenAxis] = useState(null);
  const [query, setQuery] = useState("");

  // Reset the in-panel search whenever the axis is switched.
  useEffect(() => { setQuery(""); }, [openAxis]);

  const axisValueMap = {
    care_group:   activeProvider,
    service_type: activeServiceType,
    specialism:   activeSpecialism,
  };
  const axisOptionsMap = {
    care_group:   providerOptions,
    service_type: serviceTypeOptions,
    specialism:   specialismOptions,
  };
  const axisSetterMap = {
    care_group:   onSelectProvider,
    service_type: onSelectServiceType,
    specialism:   onSelectSpecialism,
  };

  const anyActive = !!(activeProvider || activeServiceType || activeSpecialism);
  const activeCount = [activeProvider, activeServiceType, activeSpecialism].filter(Boolean).length;

  const openAxisMeta = AXES.find((a) => a.id === openAxis) || null;
  const rawOptions = openAxis ? axisOptionsMap[openAxis] : [];
  const filteredOptions = useMemo(() => {
    if (!query.trim()) return rawOptions;
    const q = query.trim().toLowerCase();
    return rawOptions.filter((o) => o.name.toLowerCase().includes(q));
  }, [rawOptions, query]);
  const maxCount = rawOptions[0]?.count || 1;

  return (
    <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden h-full w-full flex flex-col" data-testid="territory-filter-panel">
      <div className="px-5 py-4 border-b border-stone-200 flex items-center justify-between gap-3" style={{ backgroundColor: "#eeee84" }}>
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <span className="w-9 h-9 rounded-full bg-stone-950 text-[#dedd0a] flex items-center justify-center shrink-0">
            <Filter className="w-4 h-4" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-stone-900/70 truncate">
              Filter your Territory
            </div>
            <div className="text-sm text-stone-900 mt-0.5 truncate">
              {anyActive ? (
                <>
                  Showing <strong>{filteredCount}</strong> of <strong>{totalCount}</strong> home{totalCount === 1 ? "" : "s"} · <strong>{activeCount}</strong> filter{activeCount === 1 ? "" : "s"} active
                </>
              ) : (
                <>Filter across care group, service type and specialism.</>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Three category tabs — horizontal, equal-width. */}
      <div className="px-4 pt-4">
        <div className="grid grid-cols-3 gap-2">
          {AXES.map((axis) => {
            const value = axisValueMap[axis.id];
            const isOpen = openAxis === axis.id;
            const Icon = axis.icon;
            return (
              <button
                key={axis.id}
                type="button"
                onClick={() => setOpenAxis(axis.id)}
                data-testid={`territory-filter-tab-${axis.id}`}
                className={`w-full text-left rounded-xl border-2 px-3 py-2 transition-colors ${
                  // Any tab with an applied filter — OR the tab
                  // whose option list is currently being viewed —
                  // gets the highlighted yellow style so persistent
                  // cumulative filters are visible at a glance
                  // across the whole top row (mirrors mock §3
                  // "Multiple filters applied"). Only the currently
                  // viewed tab gets the darker border so the user
                  // still knows which list is open below.
                  value || isOpen
                    ? `bg-[#eeee84] ${isOpen ? "border-stone-950" : "border-stone-950/40"}`
                    : "border-stone-200 bg-white hover:bg-stone-50"
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${
                    value ? "bg-stone-950 text-[#dedd0a]" : isOpen ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-500"
                  }`}>
                    <Icon className="w-3.5 h-3.5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-stone-900 truncate">
                      {axis.label}
                    </div>
                    <div className={`text-xs mt-0.5 truncate ${value ? "text-stone-900 font-semibold" : "text-stone-500 italic"}`}>
                      {value || "— Any —"}
                    </div>
                  </div>
                  {value && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); axisSetterMap[axis.id]?.(null); }}
                      data-testid={`territory-filter-clear-${axis.id}`}
                      className="shrink-0 w-6 h-6 flex items-center justify-center rounded-md text-stone-800 hover:text-stone-950 hover:bg-stone-950/10"
                      aria-label={`Clear ${axis.label}`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Body — either the empty-state hint OR the ranked options for
          the open axis. */}
      <div className="px-4 pt-3 pb-4 flex-1 overflow-y-auto">
        {!openAxis ? (
          <div className="min-h-[220px] flex flex-col items-center justify-center text-center px-6 py-10 text-stone-500">
            <div className="text-sm font-semibold text-stone-700 mb-2">
              Select a filter above to view the available options.
            </div>
            <div className="text-xs text-stone-500 max-w-xs leading-relaxed">
              You can combine care group, service type and specialism to narrow down your territory.
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm font-bold text-stone-900">{openAxisMeta.listTitle}</div>
              {rawOptions.length > 5 && (
                <div className="relative w-56 max-w-full">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-400" />
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={openAxisMeta.searchPh}
                    data-testid={`territory-filter-search-${openAxis}`}
                    className="w-full pl-7 pr-3 py-1.5 text-xs bg-white border border-stone-300 rounded-md focus:outline-none focus:border-stone-950"
                  />
                </div>
              )}
            </div>

            {rawOptions.length === 0 ? (
              <div className="text-sm italic text-stone-500 border border-dashed border-stone-200 rounded-md px-3 py-6 text-center">
                No {openAxisMeta.label.toLowerCase()}s available given the current filters.
              </div>
            ) : filteredOptions.length === 0 ? (
              <div className="text-sm italic text-stone-500 border border-dashed border-stone-200 rounded-md px-3 py-6 text-center">
                No matches for “{query}”.
              </div>
            ) : (
              <div className="space-y-1">
                {/* Instructional helper (Feb-2026 UX tidy-up). Replaces
                    the previous "All … — no … filter" row, which read
                    too much like a real selectable result. The active
                    filter can still be cleared via the ✕ on the tab
                    header above, or via the "Clear filters" footer
                    button when one or more filters are applied. */}
                {!query.trim() && !axisValueMap[openAxis] && (
                  <div
                    data-testid={`territory-filter-helper-${openAxis}`}
                    className="px-3 py-2 text-xs italic text-stone-600"
                  >
                    Simply select a {openAxisMeta.label.toLowerCase()} below to filter.
                  </div>
                )}
                {filteredOptions.map((row) => {
                  const isActive = axisValueMap[openAxis] === row.name;
                  const barWidth = Math.max(3, Math.round((row.count / maxCount) * 100));
                  return (
                    <button
                      key={row.name}
                      onClick={() => axisSetterMap[openAxis]?.(isActive ? null : row.name)}
                      data-testid={`territory-filter-row-${openAxis}-${row.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className={`w-full group flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                        isActive ? "bg-[#eeee84]/70 ring-2 ring-stone-950" : "hover:bg-stone-50"
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-3 mb-1">
                          <div className="text-sm font-semibold text-stone-900 truncate">
                            {row.name}
                          </div>
                          <div className="shrink-0 text-xs tabular-nums font-bold text-stone-900">
                            {row.count} home{row.count === 1 ? "" : "s"}
                          </div>
                        </div>
                        <div className="h-1.5 rounded-full overflow-hidden bg-stone-100">
                          <div
                            className={`h-full rounded-full transition-all ${isActive ? "bg-stone-950" : "bg-stone-900 group-hover:bg-stone-700"}`}
                            style={{ width: `${barWidth}%` }}
                          />
                        </div>
                      </div>
                      <ChevronRight className={`w-4 h-4 shrink-0 ${isActive ? "text-stone-950" : "text-stone-400 group-hover:text-stone-700"}`} />
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Clear-all footer — sticky, only when at least one filter is
          active. Mirrors the "3. Multiple filters applied" mock. */}
      {anyActive && (
        <div className="px-4 py-3 border-t border-stone-200 bg-stone-50 flex items-center justify-between gap-3">
          <div className="text-xs text-stone-700">
            <strong>{filteredCount}</strong> home{filteredCount === 1 ? "" : "s"} matching all selected filters
          </div>
          <button
            type="button"
            onClick={() => { onClearAll?.(); setOpenAxis(null); }}
            data-testid="territory-filter-clear-all"
            className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest bg-stone-950 hover:bg-stone-800 text-white rounded-md"
          >
            Clear filters
          </button>
        </div>
      )}
    </div>
  );
}
